import clsx from "clsx";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import ArrowUp from "@assets/images/arrowup.svg";
import ArrowDown from "@assets/images/arrowdown.svg";
import UpArrow from "@assets/images/downarrow.svg";


export type TableRowId = string | number;

export type TableColumnFilterOption = {
  label: React.ReactNode;
  value: string;
};

export type TableColumnFilter<TData> = {
  options: TableColumnFilterOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (nextValue: string) => void;
  buttonClassName?: string;
  /**
   * If provided, rows will be filtered when the selected value is not `allValue`.
   */
  filterFn?: (row: TData, value: string) => boolean;
  /**
   * Value that represents "no filter" (e.g. "all"). When selected, `filterFn`
   * will not be applied.
   */
  allValue?: string;
};

export type TableColumn<TData> = {
  id: string;
  header: React.ReactNode;
  accessor?: keyof TData | ((row: TData) => unknown);
  cell?: (ctx: { row: TData; rowIndex: number; value: unknown }) => React.ReactNode;
  align?: "left" | "center" | "right";
  /**
   * Controls whether content inside the cell can visually overflow outside the
   * cell bounds (useful for dropdowns/menus inside tables).
   *
   * Defaults to "hidden" to keep long text tidy.
   */
  overflow?: "hidden" | "visible";
  className?: string;
  headerClassName?: string;
  filter?: TableColumnFilter<TData>;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  hidden?: boolean;
};

type ExpandableConfig<TData> = {
  canExpandRow?: (row: TData, rowIndex: number) => boolean;
  expandOnRowClick?: boolean;
  callRowClickOnToggle?: boolean;
  showToggleInFirstColumn?: boolean;
  expandedRowIds?: TableRowId[];
  defaultExpandedRowIds?: TableRowId[];
  onExpandedRowIdsChange?: (next: TableRowId[]) => void;
  renderExpandedRow?: (row: TData, rowIndex: number) => React.ReactNode;
  renderExpandedRowCells?: (row: TData, rowIndex: number) => React.ReactNode[];
  collapseIcon?: React.ReactNode;
  expandIcon?: React.ReactNode;
  collapseButtonAriaLabel?: string;
  expandButtonAriaLabel?: string;
  expandedRowClassName?: string | ((row: TData, rowIndex: number) => string | undefined);
  expandedCellClassName?: string | ((row: TData, rowIndex: number) => string | undefined);
};

export type TableComponentProps<TData> = {
  columns: Array<TableColumn<TData>>;
  data: Array<TData>;
  getRowId: (row: TData, rowIndex: number) => TableRowId;
  onRowClick?: (row: TData, rowIndex: number) => void;
  rowClassName?: (
    row: TData,
    rowIndex: number,
    ctx: { rowId: TableRowId; isExpanded: boolean; canExpand: boolean },
  ) => string | undefined;
  cellClassName?: (row: TData, column: TableColumn<TData>, rowIndex: number, colIndex: number) => string | undefined;
  containerClassName?: string;
  scrollContainerClassName?: string;
  disableHorizontalScroll?: boolean;
  /**
   * When enabled, cells allow wrapping instead of forcing a single line.
   * Useful for dense tables where horizontal scroll should be avoided.
   */
  wrapCells?: boolean;
  tableClassName?: string;
  headerRowClassName?: string;
  bodyClassName?: string;
  stickyHeader?: boolean;
  loading?: boolean;
  loadingState?: React.ReactNode;
  emptyState?: React.ReactNode;
  minTableWidthClassName?: string;
  expandable?: ExpandableConfig<TData>;
};

const defaultIgnoreRowClickSelector =
  "button,a,input,textarea,select,option,[role='button'],[data-table-stop-row-click='true']";

function shouldIgnoreRowClick(event: React.MouseEvent, selector = defaultIgnoreRowClickSelector) {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  return Boolean(target.closest(selector));
}

function getUnknownValue<TData>(row: TData, accessor?: keyof TData | ((r: TData) => unknown)) {
  if (!accessor) return undefined;
  if (typeof accessor === "function") return accessor(row);
  return row[accessor] as unknown;
}

export default function TableComponent<TData>(props: TableComponentProps<TData>) {
  const {
    columns,
    data,
    getRowId,
    onRowClick,
    rowClassName,
    cellClassName,
    containerClassName,
    scrollContainerClassName,
    disableHorizontalScroll = false,
    wrapCells = false,
    tableClassName,
    headerRowClassName,
    bodyClassName,
    stickyHeader = true,
    loading = false,
    loadingState,
    emptyState,
    minTableWidthClassName = "min-w-full",
    expandable,
  } = props;

  const visibleColumns = useMemo(() => columns.filter((c) => !c.hidden), [columns]);

  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<TableRowId[]>(
    expandable?.defaultExpandedRowIds ?? [],
  );

  const [uncontrolledFilterValues, setUncontrolledFilterValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const col of columns) {
      const filter = col.filter;
      if (!filter) continue;
      if (filter.value !== undefined) continue;
      const initialValue =
        filter.defaultValue ?? filter.allValue ?? filter.options?.[0]?.value ?? undefined;
      if (initialValue !== undefined) initial[col.id] = initialValue;
    }
    return initial;
  });

  useEffect(() => {
    setUncontrolledFilterValues((prev) => {
      let didChange = false;
      const next = { ...prev };
      for (const col of columns) {
        const filter = col.filter;
        if (!filter) continue;
        if (filter.value !== undefined) continue;
        if (next[col.id] !== undefined) continue;
        const initialValue =
          filter.defaultValue ?? filter.allValue ?? filter.options?.[0]?.value ?? undefined;
        if (initialValue !== undefined) {
          next[col.id] = initialValue;
          didChange = true;
        }
      }
      return didChange ? next : prev;
    });
  }, [columns]);

  const expandedRowIds = expandable?.expandedRowIds ?? uncontrolledExpandedIds;

  const canExpandRow = useMemo(() => {
    if (!expandable) return () => false;
    return expandable.canExpandRow ?? (() => true);
  }, [expandable]);

  const renderExpandedRow = expandable?.renderExpandedRow;
  const renderExpandedRowCells = expandable?.renderExpandedRowCells;

  const isExpandableEnabled = Boolean(expandable && (renderExpandedRow || renderExpandedRowCells));

  const setExpandedRowIds = (next: TableRowId[]) => {
    if (expandable?.expandedRowIds) {
      expandable.onExpandedRowIdsChange?.(next);
      return;
    }
    setUncontrolledExpandedIds(next);
    expandable?.onExpandedRowIdsChange?.(next);
  };

  const toggleExpanded = (rowId: TableRowId) => {
    if (!isExpandableEnabled) return;
    const isExpanded = expandedRowIds.includes(rowId);
    const next = isExpanded ? expandedRowIds.filter((id) => id !== rowId) : [...expandedRowIds, rowId];
    setExpandedRowIds(next);
  };

  const baseContainerClassName =
    "relative rounded-[20px] bg-[#F9FAFB] overflow-hidden border border-[#D7D7D7] mr-[10px]";
  const baseScrollClassName = "scroll-inner";
  const baseTableClassName = "w-full border-separate border-spacing-0 bg-transparent";
  const baseTheadClassName = " font-normal ";
  const baseThClassName = clsx(
    "h-[50px] py-3 px-[10px] overflow-hidden text-left font-medium capitalize bg-[#F6F7F8] text-[14px]",
    wrapCells ? "whitespace-normal break-words" : "whitespace-nowrap",
  );
  const baseTbodyClassName = "text-xs";
  const baseTdClassName = clsx(
    "p-3 text-xs font-normal text-black align-middle border-b border-[#D7D7D7] bg-inherit",
    wrapCells ? "whitespace-normal break-words" : "whitespace-nowrap",
  );

  const getFilterValue = (col: TableColumn<TData>): string | undefined => {
    const filter = col.filter;
    if (!filter) return undefined;
    return filter.value ?? uncontrolledFilterValues[col.id];
  };

  const setFilterValue = (col: TableColumn<TData>, nextValue: string) => {
    const filter = col.filter;
    if (!filter) return;
    if (filter.value === undefined) {
      setUncontrolledFilterValues((prev) => ({ ...prev, [col.id]: nextValue }));
    }
    filter.onChange?.(nextValue);
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    let next = data;
    for (const col of visibleColumns) {
      const filter = col.filter;
      if (!filter?.filterFn) continue;
      const value = getFilterValue(col);
      if (value === undefined) continue;
      if (filter.allValue !== undefined && value === filter.allValue) continue;
      next = next.filter((row) => filter.filterFn?.(row, value));
    }
    return next;
  }, [data, visibleColumns, uncontrolledFilterValues]);

  if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) return null;
  if (typeof getRowId !== "function") return null;

  if (loading) {
    return (
      <div className={clsx("w-full h-full flex items-center justify-center", containerClassName)}>
        {loadingState ?? <p className="text-gray-500 text-sm">Loading…</p>}
      </div>
    );
  }

  // Don't return null - keep table structure, just show empty tbody
  const hasData = Array.isArray(filteredData) && filteredData.length > 0;

  return (
    <div className={clsx(baseContainerClassName, containerClassName)}>
      <div
        className={clsx(baseScrollClassName, scrollContainerClassName)}
        style={{
          ...(disableHorizontalScroll ? { overflowX: "hidden" } : {}),
          overflowY: "auto",
          scrollbarGutter: "stable"

        }}
      >
        <div className="w-full">
          <div className="inline-block min-w-full align-middle">
            <table className={clsx(baseTableClassName, minTableWidthClassName, tableClassName)}>
              <colgroup>
                {visibleColumns.map((col) => {
                  return (
                    <col
                      key={col.id}
                      style={{
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                      }}
                    />
                  );
                })}
              </colgroup>
              <thead
                className={clsx(
                  "bg-[#F6F7F8] shrink-0 self-stretch",
                  baseTheadClassName,
                  stickyHeader && "sticky top-0 z-20",
                )}
              >
                <tr className={clsx(headerRowClassName)}>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.id}
                      scope="col"
                      className={clsx(
                        baseThClassName,
                        col.filter && "relative overflow-visible",
                        col.align === "center" && "text-center",
                        col.align === "right" && "text-right",
                        col.headerClassName,
                      )}
                      style={{
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                      }}
                    >
                      {col.filter ? (
                        <Menu as="div" className="relative inline-flex">
                          {({ open }) => (
                            <>
                              <Menu.Button
                                type="button"
                                className={clsx(
                                  "inline-flex w-full items-center text-[14px] justify-between gap-0",
                                  "text-[#193A6A] font-semibold hover:opacity-75 transition-opacity",
                                  col.filter.buttonClassName,
                                )}
                              >
                                <span className="truncate">{col.header}</span>
                                {open ? (
                                  <img src={UpArrow} alt="Collapse filter" />
                                ) : (
                                  <img src={ArrowDown} alt="Expand filter" />
                                )}
                              </Menu.Button>

                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items
                                  className={clsx(
                                    "absolute top-full -left-[30px] mt-1 min-w-[133px] text-[14px] flex flex-col items-stretch py-[10px] rounded-[8px] bg-white shadow-lg  focus:outline-none z-20",
                                  )}
                                >
                                  {col.filter.options.map((opt) => {
                                    const selected = opt.value === getFilterValue(col);
                                    return (
                                      <Menu.Item key={opt.value}>
                                        {({ active }) => (
                                          <button
                                            type="button"
                                            onClick={() => setFilterValue(col, opt.value)}
                                            className={clsx(
                                              "w-full text-left px-3 py-2  transition-colors",
                                              active && "bg-[#F3F4F6]",
                                              selected
                                                ? "text-[#193A6A] font-semibold bg-[#EEF5FF]"
                                                : "text-[#6B7280] hover:bg-[#F9FAFB]",
                                            )}
                                          >
                                            {opt.label}
                                          </button>
                                        )}
                                      </Menu.Item>
                                    );
                                  })}
                                </Menu.Items>
                              </Transition>
                            </>
                          )}
                        </Menu>
                      ) : (
                        col.header
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className={clsx(baseTbodyClassName, bodyClassName)}>
                {hasData ? (
                  filteredData.map((row, rowIndex) => {
                    const rowId = getRowId(row, rowIndex);
                    const isExpanded = isExpandableEnabled ? expandedRowIds.includes(rowId) : false;
                    const rowCanExpand = isExpandableEnabled ? canExpandRow(row, rowIndex) : false;

                    return (
                      <Fragment key={String(rowId)}>
                        <tr
                          data-expanded={isExpanded ? "true" : "false"}
                          onClick={(event) => {
                            if (shouldIgnoreRowClick(event)) return;
                            const shouldToggle = rowCanExpand && (expandable?.expandOnRowClick ?? true);
                            if (shouldToggle) {
                              toggleExpanded(rowId);
                              if (!expandable?.callRowClickOnToggle) return;
                            }
                            onRowClick?.(row, rowIndex);
                          }}
                          className={clsx(
                            "group bg-white transition",
                            (rowCanExpand || onRowClick) && "cursor-pointer",
                            isExpanded && rowCanExpand && "!bg-[#F7FAFF]",
                            rowClassName?.(row, rowIndex, { rowId, isExpanded, canExpand: rowCanExpand }),
                          )}
                        >
                          {visibleColumns.map((col, colIndex) => {
                            const value = getUnknownValue(row, col.accessor);
                            const content = col.cell ? col.cell({ row, rowIndex, value }) : (value as React.ReactNode);
                            const isFirstCol = colIndex === 0;
                            const isLastCol = colIndex === visibleColumns.length - 1;
                            const showToggle =
                              isFirstCol &&
                              isExpandableEnabled &&
                              rowCanExpand &&
                              (expandable?.showToggleInFirstColumn ?? true);

                            return (
                              <td
                                key={col.id}
                                className={clsx(
                                  baseTdClassName,
                                  col.overflow === "visible" ? "overflow-visible" : "overflow-hidden",
                                  col.align === "center" && "text-center",
                                  col.align === "right" && "text-right",
                                  isExpanded && rowCanExpand && "border-b-0",

                                  col.className,
                                  cellClassName?.(row, col, rowIndex, colIndex),
                                )}
                                style={{
                                  width: col.width,
                                  minWidth: col.minWidth,
                                  maxWidth: col.maxWidth,
                                }}
                              >
                                {showToggle ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      data-table-stop-row-click="true"
                                      aria-label={
                                        isExpanded
                                          ? expandable?.collapseButtonAriaLabel ?? "Collapse row"
                                          : expandable?.expandButtonAriaLabel ?? "Expand row"
                                      }
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleExpanded(rowId);
                                      }}
                                      className="text-gray-700"
                                    >
                                      {isExpanded
                                        ? expandable?.collapseIcon ?? <img src={ArrowUp} alt="Collapse row" />
                                        : expandable?.expandIcon ?? <img src={ArrowDown} alt="Expand row" />}
                                    </button>
                                    <div className="min-w-0">{content}</div>
                                  </div>
                                ) : (
                                  content
                                )}
                              </td>
                            );
                          })}
                        </tr>

                        {isExpandableEnabled && isExpanded && rowCanExpand ? (
                          renderExpandedRowCells ? (
                            (() => {
                              const cells = renderExpandedRowCells(row, rowIndex);
                              const expected = visibleColumns.length;
                              const normalized = Array.isArray(cells)
                                ? [
                                  ...cells.slice(0, expected),
                                  ...new Array(Math.max(0, expected - cells.length)).fill(null),
                                ]
                                : new Array(expected).fill(null);

                              const expandedRowClassName = expandable?.expandedRowClassName;
                              const expandedRowCls =
                                typeof expandedRowClassName === "function"
                                  ? expandedRowClassName(row, rowIndex)
                                  : expandedRowClassName;

                              const expandedCellClassName = expandable?.expandedCellClassName;
                              const expandedCellCls =
                                typeof expandedCellClassName === "function"
                                  ? expandedCellClassName(row, rowIndex)
                                  : expandedCellClassName;

                              const nonNullCells = normalized.filter((value) => value !== null && value !== undefined);
                              const isSparse = nonNullCells.length > 0 && nonNullCells.length < normalized.length;

                              if (isSparse) {
                                const expandedGridCols = Math.max(1, nonNullCells.length);
                                return (
                                  <tr className={clsx("bg-[#F7FAFF]", expandedRowCls)}>
                                    <td
                                      colSpan={expected}
                                      className={clsx(
                                        "pl-[10px] py-[16px] bg-[#F7FAFF] border-b border-[#D7D7D7]",
                                        expandedCellCls,
                                      )}
                                    >
                                      <div className="flex items-start gap-[22px] w-full">
                                        <button
                                          type="button"
                                          data-table-stop-row-click="true"
                                          aria-label={expandable?.collapseButtonAriaLabel ?? "Collapse row"}
                                          onClick={() => toggleExpanded(rowId)}
                                          className="mt-0.5 text-gray-700"
                                        >
                                          {expandable?.collapseIcon ?? <img src={ArrowUp} alt="Collapse row" />}
                                        </button>
                                        <div
                                          className="grid w-full min-w-0 grid-cols-1 gap-x-0 gap-y-0 sm:grid-cols-2 lg:[grid-template-columns:repeat(var(--expanded-grid-cols),minmax(0,1fr))]"
                                          style={
                                            {
                                              ["--expanded-grid-cols" as string]: expandedGridCols,
                                            } as React.CSSProperties
                                          }
                                        >
                                          {nonNullCells.map((cell, i) => (
                                            <div key={`${String(rowId)}:expanded:item:${i}`} className="min-w-0">
                                              {cell}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }

                              return (
                                <tr className={clsx("bg-[#F7FAFF]", expandedRowCls)}>
                                  {normalized.map((cell, colIndex) => {
                                    const col = visibleColumns[colIndex];
                                    const isFirstCol = colIndex === 0;
                                    const isLastCol = colIndex === expected - 1;
                                    return (
                                      <td
                                        key={`${String(rowId)}:expanded:${colIndex}`}
                                        className={clsx(
                                          "p-3 text-xs text-black align-top bg-inherit border-b border-[#D7D7D7]",
                                          col?.align === "center" && "text-center",
                                          col?.align === "right" && "text-right",
                                          col?.className,
                                          expandedCellCls,
                                        )}
                                        style={{
                                          width: col?.width,
                                          minWidth: col?.minWidth,
                                          maxWidth: col?.maxWidth,
                                        }}
                                      >
                                        {isFirstCol ? (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              data-table-stop-row-click="true"
                                              aria-label={expandable?.collapseButtonAriaLabel ?? "Collapse row"}
                                              onClick={() => toggleExpanded(rowId)}
                                              className="mt-0.5 text-gray-700"
                                            >
                                              {expandable?.collapseIcon ?? <ChevronUpIcon className="h-5 w-5" />}
                                            </button>
                                            <div className="min-w-0">{cell}</div>
                                          </div>
                                        ) : (
                                          cell
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })()
                          ) : (
                            <tr
                              className={clsx(
                                "bg-[#F7FAFF]",
                                (() => {
                                  const expandedRowClassName = expandable?.expandedRowClassName;
                                  return typeof expandedRowClassName === "function"
                                    ? expandedRowClassName(row, rowIndex)
                                    : expandedRowClassName;
                                })(),
                              )}
                            >
                              <td
                                colSpan={visibleColumns.length}
                                className={clsx(
                                  "p-3",
                                  (() => {
                                    const expandedCellClassName = expandable?.expandedCellClassName;
                                    return typeof expandedCellClassName === "function"
                                      ? expandedCellClassName(row, rowIndex)
                                      : expandedCellClassName;
                                  })(),
                                )}
                              >
                                {renderExpandedRow?.(row, rowIndex)}
                              </td>
                            </tr>
                          )
                        ) : null}
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td 
                      colSpan={visibleColumns.length} 
                      className={clsx("text-center border-b border-[#D7D7D7] bg-white")}
                    >
                      <div className="flex flex-col items-center justify-center h-[500px] gap-2 py-8">
                        <p className="text-gray-400 text-sm font-medium">No data available</p>
                        {emptyState && <div className="text-sm text-gray-400">{emptyState}</div>}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
